type MediaWithCounters = HTMLVideoElement & { webkitAudioDecodedByteCount?: number; webkitVideoDecodedByteCount?: number };

/**
 * Chromium's decoded-byte counters tell whether audio is actually being
 * decoded. Video advancing with zero audio bytes means the audio format is
 * unsupported (typically Dolby AC3/E-AC3) or the stream has no sound.
 * Returns `null` where the counters do not exist.
 */
export function audioIsDecoding(video: HTMLVideoElement): boolean | null {
  const media = video as MediaWithCounters;
  if (typeof media.webkitAudioDecodedByteCount !== 'number' || typeof media.webkitVideoDecodedByteCount !== 'number') return null;
  if (media.webkitVideoDecodedByteCount === 0) return null;
  return media.webkitAudioDecodedByteCount > 0;
}

/** Seconds of media buffered ahead of the playhead. */
export function bufferAhead(video: HTMLVideoElement): number {
  for (let i = 0; i < video.buffered.length; i++) {
    if (video.buffered.start(i) <= video.currentTime + 0.1 && video.currentTime <= video.buffered.end(i)) {
      return video.buffered.end(i) - video.currentTime;
    }
  }
  return 0;
}

/** End of the buffered range that contains (or starts near) the playhead. */
export function bufferedEnd(video: HTMLVideoElement): number {
  let end = 0;
  for (let i = 0; i < video.buffered.length; i++) {
    if (video.buffered.start(i) <= video.currentTime + 0.5) end = Math.max(end, video.buffered.end(i));
  }
  return end;
}

/** What actually reaches the viewer, beyond what the player reports. */
export type PictureHealth = 'ok' | 'no-picture' | 'no-sound' | 'stalled';

export interface HealthSample {
  time: number;
  frames: number | null;
  audioBytes: number | null;
}

/** Counters read every few seconds to tell a moving picture from a frozen one, and sound from silence. */
export function healthSample(video: HTMLVideoElement): HealthSample {
  const quality = typeof video.getVideoPlaybackQuality === 'function' ? video.getVideoPlaybackQuality() : null;
  const media = video as MediaWithCounters;
  return {
    time: video.currentTime,
    frames: quality ? quality.totalVideoFrames : null,
    audioBytes: typeof media.webkitAudioDecodedByteCount === 'number' ? media.webkitAudioDecodedByteCount : null,
  };
}

/**
 * Compares two samples taken a few seconds apart while the player says it is
 * playing: time must advance, new frames must be drawn (a picture exists),
 * and sound must be decoded where the browser can tell.
 */
export function pictureHealth(video: HTMLVideoElement, before: HealthSample, after: HealthSample): PictureHealth {
  if (after.time - before.time < 0.5) return 'stalled';
  if (video.videoWidth === 0 || (before.frames !== null && after.frames !== null && after.frames - before.frames < 1)) return 'no-picture';
  if (before.audioBytes !== null && after.audioBytes !== null && after.audioBytes === before.audioBytes && audioIsDecoding(video) === false) return 'no-sound';
  return 'ok';
}
